import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Close } from 'qampo-ui-library';
import SaveIcon from '../../assets/icons/save.svg?react';
import { Button } from '../UI/base/Button';
import styles from '../UI/EditableCell.module.css';

interface FooterCellProps {
  isEditing: boolean;
  onStartEdit: () => void;
  onSave: (name: string) => void;
  onCancel: () => void;
}

export const FooterCell = ({ isEditing, onStartEdit, onSave, onCancel }: FooterCellProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [prevEditing, setPrevEditing] = useState(isEditing);

  if (prevEditing !== isEditing) {
    setPrevEditing(isEditing);
    if (isEditing) setName('');
  }

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  if (!isEditing) {
    return (
      <Button
        type="tertiary"
        onClick={onStartEdit}
        styles={{ root: { fontSize: 'var(--typography-size-text-label,12px)' } }}
      >
        {t('add-room-need')}
      </Button>
    );
  }

  const trimmed = name.trim();
  const canSave = trimmed.length > 0;

  const handleSave = () => {
    if (!canSave) return;
    onSave(trimmed);
  };

  return (
    <div className={styles.editWrapper}>
      <input
        ref={inputRef}
        className={styles.input}
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          else if (e.key === 'Escape') onCancel();
        }}
      />
      <button
        type="button"
        className={`${styles.iconButton} ${styles.save}`}
        disabled={!canSave}
        onClick={handleSave}
        aria-label={t('save')}
        title={t('save')}
      >
        <SaveIcon />
      </button>
      <button
        type="button"
        className={`${styles.iconButton} ${styles.cancel}`}
        onClick={onCancel}
        aria-label={t('cancel')}
        title={t('cancel')}
      >
        <Close />
      </button>
    </div>
  );
};
